import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Slider } from './ui/slider';
import { generateAmortizationSchedule } from 'utils/mortgage-calculator/mortgageCalculations';
import { buildClassName } from 'utils/build-class-name';
import cardStyles from './ui/card.module.scss';
import styles from './InterestInsightChart.module.scss';

interface InterestInsightChartProps {
  principal: number;
  interestRate: number;
  amortization: number;
}

const InterestInsightChart = ({ principal, interestRate, amortization }: InterestInsightChartProps) => {
  const [selectedYears, setSelectedYears] = useState(Math.min(10, amortization));
  const quickSelectYears = [5, 10, 15, 20, 25].filter(y => y <= amortization);

  const schedule = generateAmortizationSchedule(principal, interestRate, amortization);

  const totalInterestForPeriod = schedule
    .slice(0, selectedYears)
    .reduce((sum, item) => sum + item.interest, 0);

  const monthlyRent = totalInterestForPeriod / (selectedYears * 12);
  const dailyRent = monthlyRent / 30;

  return (
    <Card className={buildClassName(cardStyles.shadowElevated, styles.card)}>
      <CardHeader className={styles.header}>
        <CardTitle className={styles.title}>
          <span className={styles.icon}>💵</span>
          Interest Cost Insight — &quot;Rent to Banker&quot;
        </CardTitle>
        <p className={styles.subtitle}>
          See how much of your mortgage goes to interest — money that doesn&apos;t build equity
        </p>
      </CardHeader>
      <CardContent className={styles.content}>
        {/* Period Selector */}
        <div className={styles.periodSelector}>
          <div className={styles.quickSelectButtons}>
            {quickSelectYears.map(years => (
              <button
                key={years}
                onClick={() => setSelectedYears(years)}
                className={buildClassName(
                  styles.quickSelectButton,
                  selectedYears === years && styles.active
                )}
              >
                {years} years
              </button>
            ))}
          </div>
          <div className={styles.sliderContainer}>
            <Slider
              value={[selectedYears]}
              onValueChange={value => setSelectedYears(value[0])}
              min={1}
              max={amortization}
              step={1}
            />
            <div className={styles.sliderLabels}>
              <span>1 year</span>
              <span>{amortization} years</span>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className={styles.metricsGrid}>
          <Card className={styles.metricCard}>
            <CardContent className={styles.metricContent}>
              <p className={styles.metricLabel}>Total Interest ({selectedYears}yr)</p>
              <p className={styles.metricValue}>
                ${Math.round(totalInterestForPeriod).toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card className={styles.metricCard}>
            <CardContent className={styles.metricContent}>
              <p className={styles.metricLabel}>Monthly &quot;Rent&quot;</p>
              <p className={styles.metricValue}>
                ${Math.round(monthlyRent).toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card className={styles.metricCard}>
            <CardContent className={styles.metricContent}>
              <p className={styles.metricLabel}>Daily &quot;Rent&quot;</p>
              <p className={styles.metricValue}>
                ${Math.round(dailyRent).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Chart placeholder */}
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartPlaceholderText}>Chart visualization (can be enhanced with Victory charts)</p>
        </div>

        {/* Insight Message */}
        <Card className={styles.insightCard}>
          <CardContent className={styles.insightContent}>
            <div className={styles.insightHeader}>
              <span className={styles.insightIcon}>💡</span>
              <div className={styles.insightText}>
                <p className={styles.insightMainText}>
                  In the first {selectedYears} years, you&apos;ll pay ${Math.round(monthlyRent).toLocaleString()}/month to the bank that doesn&apos;t build any equity.
                </p>
                <div className={styles.insightSubtext}>
                  <span className={styles.insightSubtextIcon}>📉</span>
                  <span>
                    <strong>Ways to reduce this &quot;rent&quot;:</strong> Increase your down payment, choose a shorter amortization, or make lump-sum payments when possible.
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
};

export default InterestInsightChart;
